import { AfterViewInit, Component, ErrorHandler, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { disabled, FieldTree, form, required, validate, ValidationError } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { AjastettuTyo, AjastettuTyoService } from '@lemon/core-firebase/eraajot/ajastettu/ajastetut-tyot.service'
import { Roolit } from '@lemon/core-firebase/model/kayttaja'
import { AsiakasLahetaPaattymisEmailTyojono, KayttajanRoolienPoistoData } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/kayttajahallinta'
import { AsiakasKopioija } from 'app/_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { AsiakkaanSopimuskausiService, LokalisoituLopettamissyy } from 'app/_jaettu-lemonator/service/sopimuskausi.service'
import { combineLatest, firstValueFrom, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs'

import { LocalDate } from '@lemon/core/model/common'
import { ComparisonOperators, DateService } from '@lemon/core/service/date.service'
import { OutgoingSlackMessage } from 'app/_jaettu-lemonator/model/integraatiot'
import { Asiakas, AsiakkaanSopimuskaudenLopettamisenSyy, AsiakkaanSopimuskausi } from '../../_jaettu-lemonator/model/asiakas'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { ApixMaksutLopetaVastaanottoJaMaksaminenTyojonoData, ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { ApixUriService } from '@lemon/core-firebase/service/apix-uri.service'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { SopimusUriService } from 'app/_jaettu-lemonator/service/sopimus-uri.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'

export interface AsiakkaanSopimuskausiPaataDialogData {
  kausi: AsiakkaanSopimuskausi
  asiakas: Asiakas
}

interface SopimuskausiLomake {
  alkaa: Date | null
  loppuu: Date | null
  lahetaPaattymassa: boolean
  lahetaPaattynyt: boolean
  tositteetLoppuu: Date | null
  syy: LokalisoituLopettamissyy | null
  lisatiedot: string
  salliMikaTahansaPaattymisPvm: boolean
}

function reaktiivisetVirheet(validaattorit: ValidatorFn[], arvo: unknown): ValidationError[] | null {
  const virheet = Validators.compose(validaattorit)?.({ value: arvo } as AbstractControl)
  return virheet ? Object.keys(virheet).map((laji): ValidationError => ({ kind: laji })) : null
}

@Component({
  templateUrl: './sopimuskausi-paata.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanSopimuskausiPaataDialog implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) sopimuskausiAlkaaInputti: MatInput

  private _asiakkaanSopimuskausiService = new AsiakkaanSopimuskausiService(this._dateService, this._timestampService)
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _lopettamisenSyy = signal<AsiakkaanSopimuskaudenLopettamisenSyy>(null)
  private readonly _salliMikaTahansaPaattymisPvmPoissa = signal(false)

  kausi: AsiakkaanSopimuskausi = null
  sopimuskausiLomake: FieldTree<SopimuskausiLomake> = null
  inputname = 'mnbiggteagjopq' + Math.random()
  minimiAlkaaPaivamaara: Date = null
  maksimiLoppuuPaivamaara: Date = null
  minimiLoppuuPaivamaara: Date = null
  minimiTositteetLoppuuPaivamaara: Date = null
  kaikkiValittavatSyyt = this._asiakkaanSopimuskausiService.lokalisoidutSopimuksenLopettamissyyt
  sopimusPaattyyPianEmailDate: LocalDate = null
  sopimusOnPaattynytEmailDate: LocalDate = null
  valittuSyy: LokalisoituLopettamissyy = null
  onkoPurku: boolean = false
  onkoLaskujenVastaanottoPaallaObservable: Observable<boolean>
  onkoMaksutPaallaObservable: Observable<boolean>

  irtisanominenMahdollistaAikaisintaanPaivamaaraObservable: Observable<LocalDate>
  private _firstPossibleEndDate: Observable<LocalDate>

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: AsiakkaanSopimuskausiPaataDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanSopimuskausiPaataDialog>,
    private _dateService: DateService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _validationService: FormValidationService,
    private _asiakasService: AsiakasService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _asiakasKopioija: AsiakasKopioija,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _firebase: FirebaseLemonator,
    private _apixUriService: ApixUriService,
    private _ajastettuTyoService: AjastettuTyoService,
    private _timestampService: TimestampService,
    private _currencyService: CurrencyService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _copyService: KopioijaPalvelu,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _sopimusUriService: SopimusUriService,
  ) {
    this.kausi = this._copyService.cloneObjectDeep(this._data.kausi)

    const edellinen = this._annaEdellinenKausi(this._data.asiakas, this.kausi)
    this.minimiAlkaaPaivamaara = edellinen?.loppuu ? this._dateService.lisaaPaivia(this._dateService.localDateToDate(edellinen.loppuu), 1) : new Date(1980, 0, 1)

    const seuraava = this._annaSeuraavaKausi(this._data.asiakas, this.kausi)
    this.maksimiLoppuuPaivamaara = seuraava?.alkaa ? this._dateService.lisaaPaivia(this._dateService.localDateToDate(seuraava.alkaa), -1) : new Date(2099, 0, 1)
    this.minimiLoppuuPaivamaara = this._dateService.lisaaPaivia(this.minimiAlkaaPaivamaara, 1)

    this._firstPossibleEndDate = of<LocalDate>(this._dateService.kuukaudenViimeinenPaikallinen(this._dateService.lisaaKuukausiaPaikallinen(this._dateService.currentLocalDate(), 1)))

    this.sopimuskausiLomake = this._annaLomakeSopimuskaudelle(this.kausi, this._data.asiakas)

    const apixConfigObservable: Observable<ApixReceivedInvoiceConfig> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of(null)
        }
        const uri = 'customers/' + asiakas.avain + '/apix-received-invoice-config/' + asiakas.avain
        return this._lemonaidFirebase.firestoreDoc<ApixReceivedInvoiceConfig>(uri).listen()
      }),
      lemonShare(),
    )

    this.onkoLaskujenVastaanottoPaallaObservable = apixConfigObservable.pipe(map(config => (config?.paymentReceiveIsActive || config?.receiveActive) ?? false))

    this.onkoMaksutPaallaObservable = apixConfigObservable.pipe(map(config => config?.paymentIsActive ?? false))
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable
      .pipe(
        map(kirjanpitajanTiedot => {
          if (kirjanpitajanTiedot) {
            return kirjanpitajanTiedot.rooli === KirjanpitajanRooli.SUPER
          }
          return false
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(onkoSuper => {
        this._salliMikaTahansaPaattymisPvmPoissa.set(!onkoSuper)
      })
    setTimeout(() => {
      this.sopimuskausiAlkaaInputti.focus()
    }, 500)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  vainKuunViimeinenFilter = (d: Date | null): boolean => {
    if (this.sopimuskausiLomake?.salliMikaTahansaPaattymisPvm().value()) {
      return true
    }
    if (d <= this.minimiLoppuuPaivamaara) {
      return false
    }
    // Syy luetaan lomakkeelta, jotta päivämäärävalitsimen tarkistus päivittyy syyn vaihtuessa kuten ennenkin.
    const valittuSyy = this.sopimuskausiLomake?.syy().value()
    // Salli vain kuun viimeinen päivä
    if (!valittuSyy?.arvo || this._asiakkaanSopimuskausiService.onkoLopetus(valittuSyy?.arvo)) {
      return this._dateService.onkoKuunViimeinen(d)
    }
    return true
  }

  private _annaSeuraavaKausi(asiakas: Asiakas, nykyinen: AsiakkaanSopimuskausi): AsiakkaanSopimuskausi {
    if (!asiakas.sopimuskaudet || !nykyinen.loppuu) {
      return null
    }
    let seuraava: AsiakkaanSopimuskausi = null
    for (const kausi of asiakas.sopimuskaudet) {
      if (kausi.avain === nykyinen.avain) {
        continue
      }
      if (this._dateService.compareLocalDates(kausi.alkaa, '>', nykyinen.loppuu)) {
        if (seuraava === null || this._dateService.compareLocalDates(seuraava.alkaa, '>', kausi.alkaa)) {
          seuraava = kausi
        }
      }
    }
    return seuraava
  }

  private _annaEdellinenKausi(asiakas: Asiakas, nykyinen: AsiakkaanSopimuskausi): AsiakkaanSopimuskausi {
    if (!asiakas.sopimuskaudet || !nykyinen.alkaa) {
      return null
    }
    let edellinen: AsiakkaanSopimuskausi = null
    for (const kausi of asiakas.sopimuskaudet) {
      if (kausi.avain === nykyinen.avain) {
        continue
      }
      if (kausi.loppuu && this._dateService.compareLocalDates(kausi.loppuu, '<', nykyinen.alkaa)) {
        if (edellinen === null || this._dateService.compareLocalDates(edellinen.loppuu, '<', kausi.loppuu)) {
          edellinen = kausi
        }
      }
    }
    return edellinen
  }

  private _annaDefaultLahetaEmailitValinta(asiakas: Asiakas): boolean {
    const nykyinenVastuukirjanpitaja = this._asiakasJaettuService.annaVastuukirjanpitajaPaivalle(asiakas, this._dateService.currentLocalDate())
    const onkoHolviAsiakas = nykyinenVastuukirjanpitaja && nykyinenVastuukirjanpitaja.kirjanpitajanAvain === 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2' // Herra holvi.
    return !onkoHolviAsiakas
  }

  private _annaLomakeSopimuskaudelle(sopimuskausi: AsiakkaanSopimuskausi, asiakas: Asiakas): FieldTree<SopimuskausiLomake> {
    const lukossa = false // lukitseVanhat && this._lukitseSopimuskausirivi(sopimuskausi, last)

    const loppuuAsDate = this._dateService.localDateToDate(sopimuskausi.loppuu)
    if (loppuuAsDate) {
      this.minimiTositteetLoppuuPaivamaara = this._dateService.lisaaPaivia(loppuuAsDate, 1)
    }

    let valittavaSyy: LokalisoituLopettamissyy = null
    if (sopimuskausi.lopettamisenSyy) {
      valittavaSyy = this._asiakkaanSopimuskausiService.annaLokalisoituSyy(sopimuskausi.lopettamisenSyy)
    }

    this.valittuSyy = valittavaSyy
    this._lopettamisenSyy.set(sopimuskausi.lopettamisenSyy)
    this.onkoPurku = this._asiakkaanSopimuskausiService.onkoPurku(sopimuskausi.lopettamisenSyy)
    this.sopimusPaattyyPianEmailDate = this._asiakkaanSopimuskausiService.annaSopimusPaattyyPianEmailDate(sopimuskausi)
    this.sopimusOnPaattynytEmailDate = this._asiakkaanSopimuskausiService.annaSopimusOnPaattynytEmailDate(sopimuskausi)

    const sopimuskausiLomake = form(
      signal<SopimuskausiLomake>({
        alkaa: this._dateService.localDateToDate(sopimuskausi.alkaa),
        loppuu: loppuuAsDate,
        lahetaPaattymassa: false,
        lahetaPaattynyt: false,
        tositteetLoppuu: this._dateService.localDateToDate(sopimuskausi.tositteidenToimitusLoppuu),
        syy: valittavaSyy ?? null,
        lisatiedot: sopimuskausi.lisatiedot ?? '',
        salliMikaTahansaPaattymisPvm: sopimuskausi.salliMikaTahansaPaattymisPvm ?? false,
      }),
      polku => {
        if (lukossa) {
          disabled(polku)
        }
        required(polku.alkaa)
        // Alkamispäivää ei voi muokata, joten se tarkistetaan vain lomakkeen luontihetken päättymispäivää vasten kuten ennenkin.
        validate(polku.alkaa, ({ value }) => reaktiivisetVirheet([this._compareToDateValidator('<', () => loppuuAsDate, 'alkaaPienempiKuinLoppuu')], value()))
        required(polku.loppuu)
        validate(polku.loppuu, ({ value, valueOf }) =>
          reaktiivisetVirheet(
            this._annaLoppuuValidators(() => valueOf(polku.alkaa), this._lopettamisenSyy(), valueOf(polku.salliMikaTahansaPaattymisPvm), asiakas),
            value(),
          ),
        )
        required(polku.tositteetLoppuu, { when: () => this._onkoTositteidenLoppuPakollinen(this._lopettamisenSyy()) })
        validate(polku.tositteetLoppuu, ({ value, valueOf }) =>
          this._onkoTositteidenLoppuPakollinen(this._lopettamisenSyy())
            ? reaktiivisetVirheet([this._compareToDateValidator('>', () => valueOf(polku.loppuu), 'tositteetSuurempiKuinLoppuu')], value())
            : null,
        )
        required(polku.syy)
        required(polku.lisatiedot, { when: ({ valueOf }) => !!valueOf(polku.syy)?.lisatiedotPakollinen })
        disabled(polku.salliMikaTahansaPaattymisPvm, () => this._salliMikaTahansaPaattymisPvmPoissa())
      },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta).pipe(takeUntil(this._ngUnsubscribe))
    // Reaktiivisen lomakkeen updateValueAndValidity lähetti kentän nykyisen arvon uudelleen tilaajille.
    const ilmoitaUudelleen = <T>(kentta: FieldTree<T>) => asetaArvo(kentta, kentta().value())

    muutokset(sopimuskausiLomake.lahetaPaattymassa).subscribe(value => {
      sopimuskausi.lahetaPaattymassaEmail = value
    })

    asetaArvo(sopimuskausiLomake.lahetaPaattymassa, sopimuskausi.lahetaPaattymassaEmail ?? this._annaDefaultLahetaEmailitValinta(asiakas))

    muutokset(sopimuskausiLomake.lahetaPaattynyt).subscribe(value => {
      sopimuskausi.lahetaPaattynytEmail = value
    })
    asetaArvo(sopimuskausiLomake.lahetaPaattynyt, sopimuskausi.lahetaPaattynytEmail ?? this._annaDefaultLahetaEmailitValinta(asiakas))

    muutokset(sopimuskausiLomake.alkaa).subscribe(value => {
      sopimuskausi.alkaa = this._dateService.dateToLocalDate(value)
      if (sopimuskausi.alkaa) {
        this.minimiLoppuuPaivamaara = this._dateService.lisaaPaivia(value, 1)
      }
    })
    muutokset(sopimuskausiLomake.loppuu).subscribe(value => {
      sopimuskausi.loppuu = this._dateService.dateToLocalDate(value)
      if (sopimuskausi.loppuu) {
        this.sopimusPaattyyPianEmailDate = this._asiakkaanSopimuskausiService.annaSopimusPaattyyPianEmailDate(sopimuskausi)
        this.sopimusOnPaattynytEmailDate = this._asiakkaanSopimuskausiService.annaSopimusOnPaattynytEmailDate(sopimuskausi)
        this.minimiTositteetLoppuuPaivamaara = this._dateService.lisaaPaivia(value, 1)
        if (!sopimuskausiLomake.tositteetLoppuu().value()) {
          const loppuvatTilikaudet = this._asiakasJaettuService.annaKuukaudenAikanaPaattyvatTilikaudet(this._data.asiakas, sopimuskausi.loppuu)
          if (loppuvatTilikaudet.length) {
            const seuraavaKuukausi = this._dateService.lisaaKuukausiaPaikallinen(sopimuskausi.loppuu, 1)
            const seuraavanKuunViimeinen = this._dateService.kuukaudenViimeinenPaikallinen(seuraavaKuukausi)
            asetaArvo(sopimuskausiLomake.tositteetLoppuu, this._dateService.localDateToDate(seuraavanKuunViimeinen))
          } else {
            const plus7Days = this._dateService.lisaaPaiviaPaikallinen(sopimuskausi.loppuu, 7)
            asetaArvo(sopimuskausiLomake.tositteetLoppuu, this._dateService.localDateToDate(plus7Days))
          }
        }
      }
      if (this.onkoPurku) {
        asetaArvo(sopimuskausiLomake.tositteetLoppuu, value)
        asetaArvo(sopimuskausiLomake.lahetaPaattymassa, false)
        asetaArvo(sopimuskausiLomake.lahetaPaattynyt, false)
      }
    })
    this.irtisanominenMahdollistaAikaisintaanPaivamaaraObservable = combineLatest([this._firstPossibleEndDate, arvonMuutokset(sopimuskausiLomake.loppuu), arvonMuutokset(sopimuskausiLomake.syy)]).pipe(
      map(([firstPossible, loppuu, syy]) => {
        if (!syy || this._asiakkaanSopimuskausiService.onkoPurku(syy.arvo)) {
          return null
        }
        const value = this._dateService.dateToLocalDate(loppuu)
        if (value && this._dateService.compareLocalDates(value, '<', firstPossible)) {
          return firstPossible
        }
        return null
      }),
    )
    muutokset(sopimuskausiLomake.syy).subscribe(value => {
      sopimuskausi.lopettamisenSyy = value?.arvo ?? null
      this._lopettamisenSyy.set(sopimuskausi.lopettamisenSyy)
      this.valittuSyy = value ?? null
      this.onkoPurku = this._asiakkaanSopimuskausiService.onkoPurku(sopimuskausi.lopettamisenSyy)
      if (this.onkoPurku) {
        asetaArvo(sopimuskausiLomake.tositteetLoppuu, sopimuskausiLomake.loppuu().value())
        asetaArvo(sopimuskausiLomake.lahetaPaattymassa, false)
        asetaArvo(sopimuskausiLomake.lahetaPaattynyt, false)
      }
      ilmoitaUudelleen(sopimuskausiLomake.tositteetLoppuu)
      ilmoitaUudelleen(sopimuskausiLomake.lisatiedot)
      ilmoitaUudelleen(sopimuskausiLomake.loppuu)
    })
    muutokset(sopimuskausiLomake.lisatiedot).subscribe(value => {
      sopimuskausi.lisatiedot = value ?? null
    })
    muutokset(sopimuskausiLomake.tositteetLoppuu).subscribe(value => {
      sopimuskausi.tositteidenToimitusLoppuu = this._dateService.dateToLocalDate(value)
    })

    muutokset(sopimuskausiLomake.salliMikaTahansaPaattymisPvm).subscribe(value => {
      sopimuskausi.salliMikaTahansaPaattymisPvm = value
      ilmoitaUudelleen(sopimuskausiLomake.loppuu)
    })

    return sopimuskausiLomake
  }

  private _onkoTositteidenLoppuPakollinen(syy: AsiakkaanSopimuskaudenLopettamisenSyy) {
    return !syy || !this._asiakkaanSopimuskausiService.onkoPurku(syy)
  }

  private _annaLoppuuValidators(alkaa: () => Date, syy: AsiakkaanSopimuskaudenLopettamisenSyy, salliMikaTahansaPaattymisPvm: boolean, asiakas: Asiakas): ValidatorFn[] {
    const validators: ValidatorFn[] = []

    if (salliMikaTahansaPaattymisPvm) {
      return validators
    }

    if (!syy || this._asiakkaanSopimuskausiService.onkoLopetus(syy)) {
      validators.push(this._lastDayOfMonthValidator('kuunViimeinen'))
    }

    validators.push(this._tarkistaMenneisyydessaNollahinta('hinnanOnMenneisyydessaOltavaNolla', asiakas))

    validators.push(this._compareToDateValidator('>', alkaa, 'loppuuSuurempiKuinAlkaa'))

    return validators
  }

  private _tarkistaMenneisyydessaNollahinta(tunniste: string, asiakas: Asiakas): ValidatorFn {
    return (ctrl: AbstractControl<Date>) => {
      if (ctrl.value) {
        // Tarkista vain menneisyydessä olevat arvot
        if (ctrl.value < new Date()) {
          const mnth = this._dateService.dateToLocalMonth(ctrl.value)
          const hinta = this._asiakasJaettuService.annaHintaKuukaudelleJosEiTauollaJaSopimusVoimassa(asiakas, mnth)
          if (hinta && hinta.hinta > 0.0) {
            const res = {}
            res[tunniste] = true
            return res
          }
        }
      }
      return null
    }
  }

  private _lastDayOfMonthValidator(tunniste: string): ValidatorFn {
    return (ctrl: AbstractControl<Date>) => {
      if (ctrl.value) {
        const asLocal = this._dateService.dateToLocalDate(ctrl.value)
        const lastOfMonth = this._dateService.kuukaudenViimeinenPaikallinen(asLocal)
        if (this._dateService.compareLocalDates(asLocal, '!=', lastOfMonth)) {
          const res = {}
          res[tunniste] = true
          return res
        }
      }
      return null
    }
  }

  private _compareToDateValidator(operator: ComparisonOperators, toinen: () => Date, tunniste: string): ValidatorFn {
    return (ctrl: AbstractControl<Date>): ValidationErrors | null => {
      // console.log('1', ctrl.value, toinen())
      if (ctrl.value && toinen()) {
        if (!this._dateService.compareLocalDates(this._dateService.dateToLocalDate(ctrl.value), operator, this._dateService.dateToLocalDate(toinen()))) {
          const res = {}
          res[tunniste] = true
          return res
        }
      }
      return null
    }
  }

  naytetaankoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  compareSyyt(o1: LokalisoituLopettamissyy, o2: LokalisoituLopettamissyy): boolean {
    return o1?.arvo === o2?.arvo
  }

  async tallenna() {
    if (!this.sopimuskausiLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.sopimuskausiLomake)
      return
    }
    this._ladataanService.aloitaLataaminen()

    const asiakasCopy = this._asiakasKopioija.kopioiAsiakas(this._data.asiakas)
    if (!asiakasCopy.sopimuskaudet) {
      asiakasCopy.sopimuskaudet = []
    }
    const index = asiakasCopy.sopimuskaudet.findIndex(k => k.avain === this.kausi.avain)
    if (index < 0) {
      throw new Error('No index found!')
    }

    for (const ruutukausi of asiakasCopy.kkruutujenKaudet ?? []) {
      if (!ruutukausi.loppuu && this._dateService.compareLocalDates(ruutukausi.alkaa, '<', this.kausi.loppuu)) {
        ruutukausi.loppuu = this.kausi.loppuu
        break
      }
    }

    if (this.onkoPurku) {
      this.kausi.tositteidenToimitusLoppuu = this.kausi.loppuu
      this.kausi.lahetaPaattymassaEmail = false
      this.kausi.lahetaPaattynytEmail = false
    }

    asiakasCopy.sopimuskaudet[index] = this.kausi

    const kirjanpitajanTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
    this.kausi.paivittaja = kirjanpitajanTiedot.uid
    this.kausi.paivitetty = this._timestampService.now()

    const batch = this._firebase.firestoreBatch()
    await this._asiakasService.lisaaAsiakasBatchiin(asiakasCopy, kirjanpitajanTiedot, 'tilikaudet-eivat-voineet-muuttua', false, false, batch)

    const poistakayttajienMuutRoolitKuinTositteetTyoAvain = this._sopimusUriService.getPoistaRoolitSailytaTositteetAvain(this.kausi.avain)
    const poistaKayttajienMuutRoolitKuinTositteetTyoUri = this._sopimusUriService.getPoistaKayttajienRoolitUri(asiakasCopy.avain, poistakayttajienMuutRoolitKuinTositteetTyoAvain)
    const poistakayttajienMuutRoolitKuinTositteet: KayttajanRoolienPoistoData = {
      asiakasAvain: asiakasCopy.avain,
      deaktivoiKayttajat: false,
      poistaKaikkiRoolit: false,
      poistettavatRoolit: null,
      sailytettavatRoolit: [Roolit.TOSITTEET_MYYNTI, Roolit.TOSITTEET_OSTO, Roolit.TOSITTEET_PALKKA, Roolit.TOSITTEET_TILI, Roolit.HALLINTO_YHTEYSHENKILO, Roolit.TUNNISTETAAN],
    }

    const roolienPoistopaiva = this._dateService.lisaaPaiviaPaikallinen(this.kausi.loppuu, 1)
    const roolienPoisto1Timestamp = this._asiakkaanSopimuskausiService.annaTimestampRoolienPoistotyolle(roolienPoistopaiva)
    const poistaKayttajienMuutRoolitKuinTositteetAjastettuTyo: AjastettuTyo<KayttajanRoolienPoistoData> = {
      ajettu: null,
      ajoaika: roolienPoisto1Timestamp,
      avain: poistakayttajienMuutRoolitKuinTositteetTyoAvain,
      kohde: poistaKayttajienMuutRoolitKuinTositteetTyoUri,
      selite: 'Poistaa asiakkaan ' + asiakasCopy.nimi + ' (' + asiakasCopy.ytunnus + ') kaikkien käyttäjien muut kuin tositteen toimitusroolit.',
      tyodata: poistakayttajienMuutRoolitKuinTositteet,
      tyyppi: 'asiakkuuden-paattaminen-poista-muut-kuin-tosite-roolit',
      asiakasAvain: asiakasCopy.avain,
    }

    if (this.onkoPurku) {
      this._ajastettuTyoService.poistaAjastusTyolla(poistaKayttajienMuutRoolitKuinTositteetAjastettuTyo, this._firebase.firestoreProvider, batch)
    } else {
      this._ajastettuTyoService.ajasta(poistaKayttajienMuutRoolitKuinTositteetAjastettuTyo, this._firebase.firestoreProvider, batch)
    }

    const poistaKayttajienKaikkiRoolitJaDeaktivoiTyoAvain = this._sopimusUriService.getPoistaRoolitJaDeaktivoiAvain(this.kausi.avain)
    const poistaKayttajienKaikkiRoolitJaDeaktivoiTyoUri = this._sopimusUriService.getPoistaKayttajienRoolitUri(asiakasCopy.avain, poistaKayttajienKaikkiRoolitJaDeaktivoiTyoAvain)
    const poistaKayttajienKaikkiRoolitJaDeaktivoi: KayttajanRoolienPoistoData = {
      asiakasAvain: asiakasCopy.avain,
      deaktivoiKayttajat: true,
      poistaKaikkiRoolit: true,
      poistettavatRoolit: null,
      sailytettavatRoolit: null,
    }

    const roolienPoisto2Timestamp = this._asiakkaanSopimuskausiService.annaTimestampKayttajienDeaktivoinnille(this.kausi)
    const poistakayttajienKaikkiRoolitJaDeaktivoiAjastettuTyo: AjastettuTyo<KayttajanRoolienPoistoData> = {
      ajettu: null,
      ajoaika: roolienPoisto2Timestamp,
      avain: poistaKayttajienKaikkiRoolitJaDeaktivoiTyoAvain,
      kohde: poistaKayttajienKaikkiRoolitJaDeaktivoiTyoUri,
      selite: 'Poistaa asiakkaan ' + asiakasCopy.nimi + ' (' + asiakasCopy.ytunnus + ') kaikkien käyttäjien kaikki roolit ja deaktivoi kaikki käyttäjät.',
      tyodata: poistaKayttajienKaikkiRoolitJaDeaktivoi,
      tyyppi: 'asiakkuuden-paattaminen-deaktivoi-kayttajat',
      asiakasAvain: asiakasCopy.avain,
    }
    this._ajastettuTyoService.ajasta(poistakayttajienKaikkiRoolitJaDeaktivoiAjastettuTyo, this._firebase.firestoreProvider, batch)

    const emailPaattymassaPvm = this._asiakkaanSopimuskausiService.annaSopimusPaattyyPianEmailDate(this.kausi)
    const emailPaattymassaTyoAvain = this._sopimusUriService.getEmailPaattymassaAvain(this.kausi.avain)
    const emailPaattymassaTyoUri = this._sopimusUriService.getEmailPaattyyUri(asiakasCopy.avain, emailPaattymassaTyoAvain)
    const emailPaattymassa: AsiakasLahetaPaattymisEmailTyojono = {
      asiakasAvain: asiakasCopy.avain,
      sopimuskausiAvain: this.kausi.avain,
      kirjanpitaja: kirjanpitajanTiedot.uid,
      tyyppi: 'lopetus_paattymassa',
      lahetysPvm: emailPaattymassaPvm,
    }
    const emailPaattymassaTimestamp = this._asiakkaanSopimuskausiService.annaTimestampSahkopostityolle(emailPaattymassaPvm)
    const lahetaEmailPaattymassaAjastettuTyo: AjastettuTyo<AsiakasLahetaPaattymisEmailTyojono> = {
      ajettu: null,
      ajoaika: emailPaattymassaTimestamp,
      avain: emailPaattymassaTyoAvain,
      kohde: emailPaattymassaTyoUri,
      selite: 'Lähettää asiakkaalle ' + asiakasCopy.nimi + ' (' + asiakasCopy.ytunnus + ') sähköpostin, joka muistuttaa sopimuksen olevan loppumassa.',
      tyodata: emailPaattymassa,
      tyyppi: 'asiakkuuden-paattaminen-laheta-paattymassa-email',
      asiakasAvain: asiakasCopy.avain,
    }

    if (this.kausi.lahetaPaattymassaEmail) {
      this._ajastettuTyoService.ajasta(lahetaEmailPaattymassaAjastettuTyo, this._firebase.firestoreProvider, batch)
    } else {
      this._ajastettuTyoService.poistaAjastusTyolla(lahetaEmailPaattymassaAjastettuTyo, this._firebase.firestoreProvider, batch)
    }

    const emailPaattynytTyoAvain = this._sopimusUriService.getEmailPaattynytAvain(this.kausi.avain)
    const emailPaattynytTyoUri = this._sopimusUriService.getEmailPaattyyUri(asiakasCopy.avain, emailPaattynytTyoAvain)
    const emailPaattynytPvm = this._asiakkaanSopimuskausiService.annaSopimusOnPaattynytEmailDate(this.kausi)
    const emailPaattynyt: AsiakasLahetaPaattymisEmailTyojono = {
      asiakasAvain: asiakasCopy.avain,
      sopimuskausiAvain: this.kausi.avain,
      kirjanpitaja: kirjanpitajanTiedot.uid,
      tyyppi: 'lopetus_paattynyt',
      lahetysPvm: emailPaattynytPvm,
    }
    const emailPaattynytTimestamp = this._asiakkaanSopimuskausiService.annaTimestampSahkopostityolle(emailPaattynytPvm)
    const lahetaEmailPaattynytAjastettuTyo: AjastettuTyo<AsiakasLahetaPaattymisEmailTyojono> = {
      ajettu: null,
      ajoaika: emailPaattynytTimestamp,
      avain: emailPaattynytTyoAvain,
      kohde: emailPaattynytTyoUri,
      selite: 'Lähettää asiakkaalle ' + asiakasCopy.nimi + ' (' + asiakasCopy.ytunnus + ') sähköpostin, joka kertoo sopimuksen loppuneen.',
      tyodata: emailPaattynyt,
      tyyppi: 'asiakkuuden-paattaminen-laheta-paattynyt-email',
      asiakasAvain: asiakasCopy.avain,
    }

    if (this.kausi.lahetaPaattynytEmail) {
      this._ajastettuTyoService.ajasta(lahetaEmailPaattynytAjastettuTyo, this._firebase.firestoreProvider, batch)
    } else {
      this._ajastettuTyoService.poistaAjastusTyolla(lahetaEmailPaattynytAjastettuTyo, this._firebase.firestoreProvider, batch)
    }

    // A copy is made out of this in the constructor, so this contains the original, un-altered data.
    // If there is no loppuu, this is the _first_ lopetus save.
    // In that case, we must send notifications to slack.
    if (!this._data.kausi.loppuu && !this._data.asiakas.testiasiakas) {
      const nykyinenHinta = this._asiakasJaettuService.annaNykyinenHinta(asiakasCopy)
      const kirjanpitajienNimitiedotMap = await firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable)
      const kirjanpitajanNimitiedot = kirjanpitajienNimitiedotMap.get(kirjanpitajanTiedot.uid)

      const slackMessageUri = 'tyojono/' + asiakasCopy.avain + '/send-slack-message/' + this.kausi.avain
      const slackMessageData: OutgoingSlackMessage = {
        channel: 'lopettavat_asiakkaat',
        created: this._timestampService.now(),
        message: `Asiakas: *${asiakasCopy.nimi}*
Hinta: ${this._currencyService.formatoiRahaIlmanValuuttaSymbolia(nykyinenHinta, 'fi')}
Sopimus päättyy: ${this._dateService.muotoilePaikallinenPaiva(this.kausi.loppuu, 'fi')}
Päättymisen syy: ${this._asiakkaanSopimuskausiService.annaLokalisoituSyy(this.kausi.lopettamisenSyy).nimi}
Lisätiedot: ${this.kausi.lisatiedot || 'Kirjanpitäjä ei kirjoittanut lisätietoja'}
Kirjanpitäjä: ${kirjanpitajanNimitiedot.etunimi + ' ' + kirjanpitajanNimitiedot.sukunimi}
Linkki: ${this._annaAsiakkaanLemonatorLinkki(asiakasCopy)}`,
        senderUid: kirjanpitajanTiedot.uid,
      }
      batch.set(slackMessageUri, slackMessageData)
    }

    const onkoLaskujenVastaanottoPaalla = await firstValueFrom(this.onkoLaskujenVastaanottoPaallaObservable)
    const onkoMaksutPaalla = await firstValueFrom(this.onkoMaksutPaallaObservable)
    if (onkoLaskujenVastaanottoPaalla || onkoMaksutPaalla) {
      const paivanLoppu = this._timestampService.dateToTimestamp(this._dateService.paivanLoppu(new Date()))
      const sopimuskausiLoppuuTimestamp = this._timestampService.localDateToTimestamp(this._dateService.lisaaPaiviaPaikallinen(this.kausi.loppuu, 1))
      const myohempiTimestamp = paivanLoppu.toMillis() > sopimuskausiLoppuuTimestamp.toMillis() ? paivanLoppu : sopimuskausiLoppuuTimestamp
      const lopetaMaksujenVastaanottoJaMaksaminenTimestamp = this.onkoPurku ? paivanLoppu : myohempiTimestamp

      const lopetaMaksujenVastaanottoJaMaksaminenTyoData: ApixMaksutLopetaVastaanottoJaMaksaminenTyojonoData = {
        asiakasAvain: asiakasCopy.avain,
      }

      const lopetaMaksujenVastaanottoJaMaksaminenTyojonAvain = this._sopimusUriService.getLopetaApixMaksujenVastaanottoJaMaksaminenAvain(this.kausi.avain)
      const lopetaMaksujenVastaanottoJaMaksaminenTyojonoUri = this._sopimusUriService.getLopetaMaksujenVastaanottoJaMaksaminenTyojonoUri(
        asiakasCopy.avain,
        lopetaMaksujenVastaanottoJaMaksaminenTyojonAvain,
      )
      const lopetaApixMaksujenVastaanottoJaMaksaminenAjastettuTyo: AjastettuTyo<ApixMaksutLopetaVastaanottoJaMaksaminenTyojonoData> = {
        ajettu: null,
        ajoaika: lopetaMaksujenVastaanottoJaMaksaminenTimestamp,
        avain: lopetaMaksujenVastaanottoJaMaksaminenTyojonAvain,
        kohde: lopetaMaksujenVastaanottoJaMaksaminenTyojonoUri,
        selite:
          'Lopettaa asiakkaan ' +
          asiakasCopy.nimi +
          ' (' +
          asiakasCopy.ytunnus +
          ') Apixin maksujen vastaanoton sopimuksen, päivittää ApixReceivingInvoiceConfig asetukset sekä tarvittaessa deaktivoi maksut.',
        tyodata: lopetaMaksujenVastaanottoJaMaksaminenTyoData,
        tyyppi: 'lopeta-apix-maksujen-vastaanotto',
        asiakasAvain: asiakasCopy.avain,
      }
      this._ajastettuTyoService.ajasta(lopetaApixMaksujenVastaanottoJaMaksaminenAjastettuTyo, this._firebase.firestoreProvider, batch)
    }

    return batch
      .commit()
      .then(async () => {
        this._asiakasService.asetaNykyinenAsiakas(asiakasCopy)
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close()
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  private _annaAsiakkaanLemonatorLinkki(asiakas: Asiakas): string {
    if (environment.environment === EnvironmentType.PRODUCTION) {
      return `https://lemonator.lemontree.fi/asiakkaat/${asiakas.avain}/sopimukset`
    } else if (environment.environment === EnvironmentType.BETA) {
      return `https://beta.lemonator.lemontree.fi/asiakkaat/${asiakas.avain}/sopimukset`
    } else if (environment.environment === EnvironmentType.DEV) {
      return `https://dev.lemonator.lemontree.fi/asiakkaat/${asiakas.avain}/sopimukset`
    } else if (environment.environment === EnvironmentType.LOCAL_DEV) {
      return `https://local-dev.lemonator.lemontree.fi/asiakkaat/${asiakas.avain}/sopimukset`
    }
    throw new Error('Unknown environment ' + environment.environment)
  }
}
